import type { SajuAnalysis } from '../types/input';
import type { TwelveStageCross } from '../types/compatibility';
import type { TwelveStage } from '../types/input';
import { getTwelveStage } from '../analysis/twelve-stages';

const STRONG_STAGES: TwelveStage[] = ['장생', '관대', '건록', '제왕'];
const WEAK_STAGES: TwelveStage[] = ['병', '사', '묘', '절'];

/**
 * 두 사주의 십이운성 교차 분석.
 *
 * P1 일간이 P2 일지에서 어떤 운성인지 (= P1이 P2 공간에서 얼마나 활력이 있는지)
 * P2 일간이 P1 일지에서 어떤 운성인지 (= P2가 P1 공간에서 얼마나 활력이 있는지)
 *
 * - 쌍왕: 양쪽 모두 강운성 → 활기차지만 경쟁 가능
 * - 쌍약: 양쪽 모두 약운성 → 에너지 부족
 * - 왕약상보: 한쪽 강 + 한쪽 약 → 상보적
 * - 균형: 중간 운성
 */
export function analyzeTwelveStageCross(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): TwelveStageCross {
  // P1 일간이 P2 일지에서의 운성
  const p1AtP2 = getTwelveStage(analysis1.dayMaster, analysis2.fourPillars.day.branch);
  // P2 일간이 P1 일지에서의 운성
  const p2AtP1 = getTwelveStage(analysis2.dayMaster, analysis1.fourPillars.day.branch);

  // 각자의 일지 운성 (자기 일간 기준)
  const p1Own = analysis1.twelveStages.day;
  const p2Own = analysis2.twelveStages.day;

  const isStrong1 = STRONG_STAGES.includes(p1AtP2.stage);
  const isStrong2 = STRONG_STAGES.includes(p2AtP1.stage);
  const isWeak1 = WEAK_STAGES.includes(p1AtP2.stage);
  const isWeak2 = WEAK_STAGES.includes(p2AtP1.stage);

  let pattern: TwelveStageCross['pattern'];
  let harmony: TwelveStageCross['harmony'];

  if (isStrong1 && isStrong2) {
    pattern = '쌍왕';
    harmony = '보통'; // 활기차지만 경쟁
  } else if (isWeak1 && isWeak2) {
    pattern = '쌍약';
    harmony = '주의';
  } else if ((isStrong1 && isWeak2) || (isWeak1 && isStrong2)) {
    pattern = '왕약상보';
    harmony = '좋음';
  } else {
    pattern = '균형';
    harmony = '보통';
  }

  const patternLabel: Record<TwelveStageCross['pattern'], string> = {
    '쌍왕': '양쪽 모두 상대 공간에서 왕성',
    '쌍약': '양쪽 모두 상대 공간에서 약함',
    '왕약상보': '한쪽이 상대 공간에서 강하여 상보적',
    '균형': '중간 수준의 에너지 교환',
  };

  const description = `운성교차: P1→P2(${p1AtP2.stage}) P2→P1(${p2AtP1.stage}) — ${patternLabel[pattern]}`;

  return {
    person1StageAtPerson2Day: p1AtP2.stage,
    person2StageAtPerson1Day: p2AtP1.stage,
    person1OwnDayStage: p1Own.stage,
    person2OwnDayStage: p2Own.stage,
    pattern,
    harmony,
    description,
  };
}
